import React, { useMemo, useRef, useState, useEffect } from 'react';
import CardBox from '@/pages/dashboard/components/CardBox';
import { useIntl} from 'umi';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { getLanguage, getName } from '@home/utils/common';
import { Segment } from 'tea-component';
import classNames from 'classnames';
import FilterSelect from '@/pages/dashboard/components/FilterSelect';
import { LANGUAGE_CN, LANGUAGE_EN } from '@home/_config/config';
import Abnormal from '@home/components/Abnormal';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { handleDownload } from '@/pages/dashboard/utils/utils';
import { ChartRef } from '@/pages/dashboard/components/Chart/_config/interface';
import { IMarkTrendProps } from './type';
import { useChartData } from './hooks/useChartData';
import { useChartOptions } from './hooks/useChartOptions';
import { useTableData } from './hooks/useTableData';
import { createColorMapAndLegend, processBaseData, processDateMap } from './utils/chartUtils';
import './index.scss';

const MarkAreaTrend = (props: IMarkTrendProps) => {
  const {
    className,
    data: propsData,
    dataKey,
    selectorKey,
    loadingHeight,
    titleEn,
    titleCn,
    subTitleEn,
    subTitleCn,
    dateNameMap,
    tabList,
    maxSelect = 5,
    minSelect,
    initialCompetitor = [],
    downloadFileTitleEn,
    downloadFileTitleCn,
    formatRule,
    downloadFormatRule,
    tableHeight = 320,
    changeData,
    tooltipConfig,
  } = props;
  
  const { data, loading, errorMsg: error } = propsData?.[dataKey] || {};
  const { competitor_list: competitorList } = propsData?.[selectorKey]?.data || {};
  const { formatMessage } = useIntl();
  const lineChartRef = useRef<ChartRef>(null);
  const { isEN } = useLocal();
  const title = getName(isEN, titleEn, titleCn);
  const downLoadTitle = getName(isEN, downloadFileTitleEn, downloadFileTitleCn);
  const [selectedSegment, setSelectedSegment] = useState(tabList?.[0]?.value || '');
  const currentTab = tabList?.find((tab: { value: string; }) => tab.value === selectedSegment);
  const [competitor, setCompetitor] = useState(initialCompetitor);
  const [visibleSeries, setVisibleSeries] = useState<string[]>([]);

  // 处理legend点击事件
  const handleLegendSelectChange = (visibleSeriesList: string[]) => {
    setVisibleSeries(visibleSeriesList);
  };

  // 处理基础数据
  const { subTitle, tableDateMap, lastDate, lastData, xData, lastOriginData } = useMemo(() => 
    processBaseData({ data, isEN, subTitleEn, subTitleCn, formatRule }), 
  [formatRule, isEN, data?.list, subTitleCn, subTitleEn]
  );

  // 处理日期映射
  const { dateMap, tooltipDateMap } = useMemo(() => 
    processDateMap(data), 
  [data?.list]
  );

  // 创建颜色映射和图例数据
  const { colorMap, legendData, colorArr } = useMemo(() => 
    createColorMapAndLegend({ currentTab, isEN, selectedSegment, data, competitorList }), 
  [currentTab, isEN, selectedSegment, data, competitorList]
  );

  // 使用自定义hooks
  const { series, formatterFunc } = useChartData({
    data,
    currentTab,
    selectedSegment,
    isEN,
    competitorList,
    formatRule,
    lastOriginData,
    dateMap,
    tooltipDateMap,
    legendData,
    colorMap,
    tooltipConfig,
    dateNameMap,
    visibleSeries,
  });

  // 初始化visibleSeries，当series数据变化时更新
  useEffect(() => {
    if (series && series.length > 0) {
      let initialVisibleSeries: string[] = [];
      initialVisibleSeries = series?.map((item: any) => item.name)?.filter(Boolean);
      setVisibleSeries(initialVisibleSeries);
    }
  }, [series, selectedSegment, currentTab, isEN]);

  const { mergeOptions } = useChartOptions({
    selectedSegment,
    colorArr,
    legendData,
    colorMap,
    currentTab,
    isEN,
    dateMap,
    dateNameMap,
    xData,
    lastData,
    visibleSeries
  });

  const { tableData, downloadData, columns } = useTableData({
    data,
    isEN,
    tableDateMap,
    dateNameMap,
    formatRule,
    downloadFormatRule,
    lastDate,
    competitorList,
    currentTab
  });

  // Tab切换选项
  const segmentOption = useMemo(() => tabList?.map((item: any) => {
    const { nameEn, nameCn, value} = item;
    return {
      text: isEN ? nameEn : nameCn,
      value
    };
  }), [tabList, isEN]);
  
  // 竞品下拉数据
  const sources = useMemo(() => [
    ...(competitorList?.map((item: { combined_id: string; game_name: string; game_icon_url: string; }) => ({
      text: item?.game_name,
      value: item?.combined_id,
      render: (item: { text: string; }) => (
        <span dangerouslySetInnerHTML={{__html: `${item?.text}`}}></span>
      ),
    })) || [])
  ], [competitorList]);

  // Tab切换组件
  const segmentNode = (
    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: "10px" }}>
      <Segment
        options={segmentOption}
        className={segmentOption.length ? '' : 'segment-empty'}
        value={selectedSegment}
        onChange={(value) => {
          setSelectedSegment?.(value);
          if (value === 'competitor') {
            setCompetitor(initialCompetitor);
            changeData('competitor_list', initialCompetitor);
          } else {
            changeData('competitor_list', []);
          }
        }}
      />
    </div>
  );

  // 渲染列表
  const renderList = useMemo(() => [
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: 
      <>
        {segmentNode}
        <Abnormal
          style={{ height: loadingHeight ?? 320 }}
          component={
            <>
              <div className="dbd-forecast-mark-area-trend-subtitle">{subTitle}</div>
              <LineChart
                height={340}
                ref={lineChartRef}
                setOtherConfigFunc={mergeOptions}
                legendData={legendData}
                xAxisData={xData}
                series={series}
                formatterFunc={formatterFunc}
                onLegendSelectChange={handleLegendSelectChange}
              />
            </>
          }
          isLoading={loading}
          isError={error}
          isNoData={!data}
        />
      </>
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: 
      <>
        {segmentNode}
        <MiniTable
          loading={loading}
          tableOption={columns || []}
          tableList={tableData || []}
          height={tableHeight}
        />
      </>,
    },
  ], [formatMessage, segmentNode, loadingHeight, subTitle, legendData, xData, series, formatterFunc, loading, error, data, columns, tableData, tableHeight]);

  return (
    <CardBox
      className={classNames('dbd-forecast-mark-area-trend', className)}
      titleCom={title}
      renderList={renderList}
      isLoading={false}
      isError={false}
      isNodata={false}
      failedMsg={formatMessage({ id: 'common_loading_failed_realtime'})}
      downloadClick={
        !loading
          ? () =>
            handleDownload(
              { data: tableData, option: columns, downloadData },
              downLoadTitle || title,
              false
            )
          : undefined
      }
      extraOperationSlot={(selectedSegment === "competitor" && !loading) &&
        <FilterSelect
          value={competitor}
          locale={isEN ? LANGUAGE_EN : LANGUAGE_CN}
          className='dbb-select-data-source'
          defaultValue={initialCompetitor}
          name={getLanguage(isEN, 'Competitor', '竞品')}
          tooltip=''
          listWidth={245}
          clickClose={false}
          closeOnScroll={false}
          onConfirm={(val) => {
            setCompetitor(val);
            changeData("competitor_list", val.length === 0 ? [""] : val);
          }}
          isRevertToLastSelection={true}
          options={sources}
          maxSelect={maxSelect}
          minSelect={minSelect}
        />
      }   
    />
  );
};

export default MarkAreaTrend;
